import { useCallback, useRef } from "react";
import { Controller, FieldValues, UseControllerProps } from "react-hook-form";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import type { SxProps } from "@mui/material/styles";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { format, isValid, parse } from "date-fns";

import { DATE_MASK } from "components/date-string-form-field/date-string-form-field.util";
import { isDateStringValid } from "utils/common";

type DateStringFormFieldProps = {
  sx?: SxProps;
  label: string;
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  PopperProps?: any;
  /** Render the input without its visible label; `label` still names the
      field for screen readers and validation messages. For layouts where a
      separate row label already names the field (ENG-1041 status modal). */
  hideLabel?: boolean;
  /** Show the picker's native in-field clear (✕) button when a value is set. */
  clearable?: boolean;
  /** Override the default "<label> is required" validation message. */
  requiredMessage?: string;
  allowFutureDateOnly?: boolean;
  quickOptions?: string[];
  quickOptionsPlacement?: "inline" | "below";
  quickOptionBaseDate?: Date | null;
  triggerInputOnBlur?: boolean;
  /** `anchorDate` is the date the user entered or the record loaded with --
      never one a quick option produced, so switching options re-applies to the
      same date instead of stacking (DEV-3418). Null when the field was empty. */
  onQuickOptionClick?: (
    value: string,
    anchorDate: Date | null,
    baseDate?: Date | null
  ) => Date | null;
  onBlur?: () => void;
  additionalValidation?: (value: any) => boolean | string;
};

type RhfDateStringFormFieldProps<T extends FieldValues> =
  DateStringFormFieldProps & UseControllerProps<T>;

export default function DateStringFormField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  autoFocus,
  PopperProps,
  placeholder = DATE_MASK.toUpperCase(),
  readOnly = false,
  disabled = false,
  hideLabel = false,
  clearable = false,
  requiredMessage,
  allowFutureDateOnly,
  sx,
  triggerInputOnBlur,
  onBlur: onBlurCallback,
  additionalValidation,
  quickOptions,
  quickOptionsPlacement = "inline",
  quickOptionBaseDate,
  onQuickOptionClick
}: RhfDateStringFormFieldProps<T>) {
  const showInline =
    quickOptionsPlacement === "inline" && Boolean(quickOptions?.length);
  const showBelow =
    quickOptionsPlacement === "below" && Boolean(quickOptions?.length);
  // The value the last quick option wrote, and the anchor it was computed from.
  // While the field still holds that value, the next option reuses the anchor.
  const lastQuickOption = useRef<{
    value: string;
    anchor: Date | null;
  } | null>(null);
  const validate = useCallback(
    (value: any) => {
      if (readOnly || disabled) return true;

      const date = value && parse(value, DATE_MASK, new Date());
      if (date && !isValid(date)) {
        return `The ${label || "date"} is not valid (MM/DD/YYYY).`;
      }
      if (date && isValid(date)) {
        const year = date.getFullYear();
        if (year < 1900 || year > 2100) {
          return `The ${label || "date"} has an invalid year.`;
        }
      }
      if (date && allowFutureDateOnly && date <= new Date()) {
        return "Date should be in the future";
      }

      if (additionalValidation) {
        return additionalValidation?.(date);
      }

      return true;
    },
    [allowFutureDateOnly, readOnly, disabled, label, additionalValidation]
  );

  return (
    <Box
      display="flex"
      {...(showBelow ? { flexDirection: "column" } : { alignItems: "center" })}
      sx={{
        pr: 4,
        ...sx
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Controller
          control={control}
          name={name}
          rules={{
            ...(required
              ? {
                  required: requiredMessage ?? `${label || "date"} is required`
                }
              : {}),
            validate
          }}
          render={({
            field: { onBlur, name, value, ...field },
            fieldState
          }) => {
            function handleNewDateValue(newValue: any, field: any) {
              if (newValue === null) {
                field.onChange("");
              } else if (!isNaN(newValue) && newValue instanceof Date) {
                field.onChange(format(newValue, DATE_MASK));
              } else {
                field.onChange(newValue !== "" ? "Invalid Date" : newValue);
              }
            }

            const currentDate =
              value && isValid(parse(value, DATE_MASK, new Date()))
                ? parse(value, DATE_MASK, new Date())
                : null;

            function applyQuickOption(option: string) {
              if (!onQuickOptionClick) return;
              const anchor =
                value && value === lastQuickOption.current?.value
                  ? lastQuickOption.current.anchor
                  : currentDate;
              const newValue = onQuickOptionClick(
                option,
                anchor,
                quickOptionBaseDate
              );
              lastQuickOption.current = newValue
                ? { value: format(newValue, DATE_MASK), anchor }
                : null;
              handleNewDateValue(newValue, field);
              onBlur();
              onBlurCallback?.();
            }

            return (
              <>
                <DesktopDatePicker
                  {...field}
                  views={["year", "month", "day"]}
                  value={value ? parse(value, DATE_MASK, new Date()) : null}
                  disabled={disabled}
                  slotProps={{
                    field: clearable ? { clearable: true } : undefined,
                    textField: {
                      required,
                      placeholder,
                      disabled,
                      inputProps: hideLabel
                        ? { "aria-label": label }
                        : undefined,
                      InputProps: {
                        autoFocus,
                        readOnly,
                        spellCheck: false,
                        onBlur: triggerInputOnBlur ? onBlurCallback : undefined,
                        // Only claim endAdornment when quick options render
                        // inline — an explicit `undefined` here clobbers the
                        // clear (✕) adornment the picker injects for
                        // `clearable`.
                        ...(showInline && {
                          endAdornment: (
                            <Stack
                              direction="row"
                              justifyContent="flex-end"
                              alignItems="center"
                              mr={-1.6}
                              width="220px"
                            >
                              {quickOptions!.map((option) => (
                                <ToggleButton
                                  key={option}
                                  value={option}
                                  size="small"
                                  sx={{
                                    fontSize: "12px",
                                    px: 0.75,
                                    borderRadius: 0
                                  }}
                                  onClick={() => applyQuickOption(option)}
                                >
                                  {option}
                                </ToggleButton>
                              ))}
                            </Stack>
                          )
                        })
                      },
                      onBlur,
                      name,
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message
                    },
                    popper: PopperProps,
                    inputAdornment: {
                      position: "start",
                      sx: {
                        display: readOnly ? "none" : undefined,
                        marginRight: showInline ? "-13px" : undefined
                      }
                    }
                  }}
                  onChange={(newValue: any) => {
                    if (isDateStringValid(newValue) || !newValue) {
                      handleNewDateValue(newValue, field);
                      onBlurCallback?.();
                    }
                  }}
                  label={hideLabel ? undefined : label}
                  onAccept={(newValue) => {
                    if (isDateStringValid(newValue) || !newValue) {
                      handleNewDateValue(newValue, field);
                      onBlur();
                      onBlurCallback?.();
                    }
                  }}
                />
                {showBelow && (
                  <Stack direction="row" mt={0.5} flexWrap="wrap">
                    {quickOptions!.map((option) => (
                      <ToggleButton
                        key={option}
                        value={option}
                        size="small"
                        sx={{
                          fontSize: "12px",
                          px: 0.75
                        }}
                        onClick={() => applyQuickOption(option)}
                      >
                        {option}
                      </ToggleButton>
                    ))}
                  </Stack>
                )}
              </>
            );
          }}
        />
      </LocalizationProvider>
    </Box>
  );
}
